import { useContext, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { THEME_ENUM } from 'constants/theme.constant'
import { setMode } from 'store/theme/themeSlice'
import { CompanyContext } from 'contexts/CompanyContext'
import { AppSumoContext } from 'contexts/AppSumoContext'
import twttr from 'twitter-text'
import { stripHTML } from 'utils/stripHTML'
import getFileType, { getFileTypeImageVideo } from 'utils/fileType'
import getVideoDuration from 'utils/getVideoDuration'
import getFileSize from 'utils/getFileSize'
import { useTranslation } from 'react-i18next'
import capitalize from 'components/ui/utils/capitalize'
import { getImageDimensions } from 'utils/getImageWidthHeight'
import { checkIfIsIntegrated } from 'utils/checkIfIsIntegrated'
import { checkIfIsExpired } from 'utils/checkIfIsExpired'
import { getNetworkCharCountUnformatted } from 'components/layout/MagicEditor/OptionsBar'
import { getVideoDimensions } from 'utils/getVideoDimensions'
import { socialNetworksMediaProductTypes } from 'views/calendar/SocialNetworkSidePanel'
import { getPostValidationConstants, imageFormatsLabel, isImageFormatAllowed } from 'utils/postValidationConstants'

export const getNetworkValidationIndex = (network, media_product_type) => {
    if (socialNetworksMediaProductTypes[network]?.includes(media_product_type)) {
        return `${network}_${(media_product_type || "FEED")?.toLowerCase()}`;
    } else {
        return `${network}_${socialNetworksMediaProductTypes[network]?.[0]?.toLowerCase()}`;
    }
}

function usePostValidator() {
    
    const { companySocials } = useContext(CompanyContext);
    const { isSumoling, isLicensee } = useContext(AppSumoContext);
    // AppSumo accounts (sumolings and licensees) can't publish tweets that contain links
    const twitterLinksAllowed = !isSumoling() && !isLicensee();
    const [ validationConstants, setValidationConstants ] = useState({});
    const [ videoDurations, setVideoDurations ] = useState({});
    const [ fileSizes, setFileSizes ] = useState({});
    const [ imageDimensions, setImageDimensions ] = useState({});
    const { t } = useTranslation();

    const _getNetworkPostName = (network, media_product_type) => {
        return `${capitalize(network)}${media_product_type !== "FEED" ? ` ${capitalize(media_product_type?.toLowerCase())}` : ""}`
    }

    const _getVideoDuration = async (uri) => {
        if (videoDurations?.[uri]) {
            return videoDurations?.[uri];
        }
        const response = await getVideoDuration(uri);
        if (response) {
            let _new = videoDurations;
            _new[uri] = response;
            setVideoDurations(_new);
            return response;
        }
        return null;
    }

    const _getFileSize = async (uri) => {
        if (fileSizes?.[uri]) {
            return fileSizes?.[uri];
        }
        try {
            const response = await getFileSize(uri);
            if (response) {
                let _new = fileSizes;
                _new[uri] = response;
                setFileSizes(_new);
                return response;
            }
        } catch {}
        return null;
    }

    const _getImageDimensions = async (uri) => {
        if (imageDimensions?.[uri]) {
            return imageDimensions?.[uri];
        }
        const { width, height } = await getImageDimensions(uri);
        if (width && height) {
            let _new = imageDimensions;
            _new[uri] = { width, height };
            setImageDimensions(_new);
            return _new[uri];
        }
        return null;
    }

    const _getImageRatio = async (uri) => {
        const { width, height } = await _getImageDimensions(uri);
        return parseFloat(width / height).toFixed(2);
    }

    const validate = async (posts) => {
        var errors = await getErrors(posts);
        return errors?.length > 0 ? false : true;
    }

    const getFirstError = async (posts) => {
        const errors = await getErrors(posts);

        return errors?.length > 0 ? errors[0]?.error : null;
    }

    // Tagged with a code so callers can tell it apart from the other rules.
    const _getDescriptionLengthError = (post, description) => {
        const constants = validationConstants?.[getNetworkValidationIndex(post?.network, post?.preferences?.media_product_type)];
        if (constants?.description?.max_length === undefined) return null;
        // Threads counts like the API's publish check (mb_strlen, code points): an emoji is one
        // character there, not the two UTF-16 units .length gives.
        const count = post?.network === "threads"
            ? Array.from(stripHTML(description || "")).length
            : getNetworkCharCountUnformatted(post?.network, description);
        if (count <= constants?.description?.max_length) return null;
        return { network: post?.network, code: 'descriptionLength', error: t(`postErrors.descriptionLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.description?.max_length }) };
    }

    // Only the text length rule: no media lookups and no changes to the posts, so it is cheap
    // enough to run right before a save.
    const getDescriptionLengthErrors = (posts) => {
        return (posts || [])
            .map((post) => _getDescriptionLengthError(post, post?.description || post?.text))
            .filter(Boolean);
    }

    const getErrors = async (posts) => {
        let errors = [];

        for (let j = 0; j < posts?.length; j++) {

            let post = posts[j];
            post.media_urls = post?.media_urls || post?.assets?.map((asset) => asset?.image?.url || asset?.video?.url);
            post.description = post?.description || post?.text;
            const networkValidationIndex = getNetworkValidationIndex(post?.network, post?.preferences?.media_product_type);
            const constants = validationConstants?.[networkValidationIndex];

            if (constants) {

                // connected network
                if (constants?.connection?.required) {
                    const socialNetworkIntegrationObj = companySocials?.filter(e => e.type === post?.network)?.[0];
                    const isIntegrated = checkIfIsIntegrated(post?.network, companySocials);
                    const isExpired = isIntegrated && checkIfIsExpired(socialNetworkIntegrationObj);
                    if (!isIntegrated || isExpired) errors.push({ network: post?.network, error: t(`postErrors.connectionRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }

                // empty post
                if (!constants?.description?.required && !constants?.media_urls?.required) {
                    if (!post?.description && !post?.media_urls?.length) errors.push({ network: post?.network, error: t(`postErrors.postEmpty`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }

                // title
                if (constants?.title?.required == true) {
                    if (!post?.title) errors.push({ network: post?.network, error: t(`postErrors.titleRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }
                if (constants?.title?.max_length !== undefined) {
                    if (post?.title?.length > constants?.title?.max_length) errors.push({ network: post?.network, error: t(`postErrors.titleLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.title?.max_length }) });
                } 

                // board_id
                if (constants?.board_id?.required == true) {
                    if (!post?.preferences?.board_id) errors.push({ network: post?.network, error: t(`postErrors.boardRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }

                // privacy_level
                if (constants?.privacy_level?.required == true) {
                    if (!post?.preferences?.privacy_level) errors.push({ network: post?.network, error: t(`postErrors.privacyLevel`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }

                // description
                if (constants?.description?.required == true) {
                    if (!stripHTML(post?.description)) errors.push({ network: post?.network, error: t(`postErrors.descriptionRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }
                const descriptionLengthError = _getDescriptionLengthError(post, post?.description);
                if (descriptionLengthError) errors.push(descriptionLengthError);
                if (constants?.description?.allow_links == false) {
                    if (twttr.extractUrls(post?.description || "")?.length > 0) errors.push({ network: post?.network, error: t(`postErrors.descriptionLinks`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }

                // media_urls
                if (constants?.media_urls?.required == true) {
                    if (!post?.media_urls?.length) errors.push({ network: post?.network, error: t(`postErrors.mediaRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }
                if (constants?.media_urls?.allow_multiple_types == false) {
                    if (post?.media_urls?.filter(url => getFileType(url) == "image")?.length > 0 && post?.media_urls?.filter(url => getFileType(url) == "video")?.length > 0) errors.push({ network: post?.network, error: t(`postErrors.combineMediaTypes`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }
                if (constants?.media_urls?.max_length !== undefined) {
                    if (post?.media_urls?.length > constants?.media_urls?.max_length) errors.push({ network: post?.network, error: t(`postErrors.mediaLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.media_urls?.max_length }) });
                }
                if (constants?.media_urls?.max_images_length !== undefined) {
                    if (post?.media_urls?.filter(url => getFileType(url) == "image")?.length > constants?.media_urls?.max_images_length) errors.push({ network: post?.network, error: t(`postErrors.mediaImagesLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.media_urls?.max_images_length }) });
                }
                if (constants?.media_urls?.max_videos_length !== undefined) {
                    if (post?.media_urls?.filter(url => getFileType(url) == "video")?.length > constants?.media_urls?.max_videos_length) errors.push({ network: post?.network, error: t(`postErrors.mediaVideosLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.media_urls?.max_videos_length }) });
                }
                if (constants?.media_urls?.max_gifs_length !== undefined) {
                    if (post?.media_urls?.filter(url => getFileType(url) == "gif")?.length > constants?.media_urls?.max_gifs_length) errors.push({ network: post?.network, error: t(`postErrors.mediaGifsLength`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_length: constants?.media_urls?.max_gifs_length }) });
                }
                if (constants?.media_urls?.image_max_size_bytes !== undefined) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {
                        if (getFileType(post?.media_urls[i]) == "image") {
                            if (await _getFileSize(post?.media_urls[i]) > constants?.media_urls?.image_max_size_bytes) errors.push({ network: post?.network, error: t(`postErrors.imageMaxFileSize`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_size: constants?.media_urls?.image_max_size_bytes / 1024 / 1024 }) });
                        }
                    }
                }
                if (constants?.media_urls?.image_formats !== undefined) {
                    const formats = constants.media_urls.image_formats;
                    if (post?.media_urls?.some(url => getFileType(url) === "image" && !isImageFormatAllowed(url, formats))) errors.push({ network: post?.network, error: t(`postErrors.imageFormat`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), formats: imageFormatsLabel(formats) }) });
                }
                if (constants?.media_urls?.video_max_size_bytes !== undefined) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {
                        if (getFileType(post?.media_urls[i]) == "video") {
                            if (await _getFileSize(post?.media_urls[i]) > constants?.media_urls?.video_max_size_bytes) errors.push({ network: post?.network, error: t(`postErrors.videoMaxFileSize`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_size: constants?.media_urls?.video_max_size_bytes / 1024 / 1024 }) });
                        }
                    }
                }
                if (constants?.media_urls?.video_min_duration_seconds !== undefined) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {
                        if (getFileType(post?.media_urls[i]) == "video") {
                            if (await _getVideoDuration(post?.media_urls[i]) < constants?.media_urls?.video_min_duration_seconds) errors.push({ network: post?.network, error: t(`postErrors.videoMinDuration`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_duration: constants?.media_urls?.video_min_duration_seconds }) });
                        }
                    }
                }
                if (constants?.media_urls?.video_max_duration_seconds !== undefined) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {
                        if (getFileType(post?.media_urls[i]) == "video") {
                            if (await _getVideoDuration(post?.media_urls[i]) > constants?.media_urls?.video_max_duration_seconds) errors.push({ network: post?.network, error: t(`postErrors.videoMaxDuration`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), min_duration: constants?.media_urls?.video_max_duration_seconds }) });
                        }
                    }
                }
                if (constants?.media_urls?.same_ratio == true) {
                    let ratios = [];
                    for (let i = 0; i < post?.media_urls?.filter(url => getFileTypeImageVideo(url) == "image").length; i++) ratios.push(await _getImageRatio(post?.media_urls[i]));
                    if (ratios?.length && !ratios.every(v => v === ratios[0])) errors.push({ network: post?.network, error: t(`postErrors.sameRatio`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),}) });
                }
                if (constants?.media_urls?.video_max_width !== undefined || constants?.media_urls?.video_min_width !== undefined) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {
                        if (getFileType(post?.media_urls[i]) == "video") {
                            const { width } = await getVideoDimensions(post?.media_urls[i]);
                            if (!!constants?.media_urls?.video_max_width && width > constants?.media_urls?.video_max_width) {
                                errors.push({ network: post?.network, error: t(`postErrors.videoMaxWidth`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), max_width: constants?.media_urls?.video_max_width }) });
                            }
                            if (!!constants?.media_urls?.video_min_width && width < constants?.media_urls?.video_min_width) {
                                errors.push({ network: post?.network, error: t(`postErrors.videoMinWidth`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type), min_width: constants?.media_urls?.video_min_width }) });
                            }
                        }
                    }
                }

                if (constants?.media_urls?.allowed_aspect_ratios?.length > 0) {
                    for (let i = 0; i < post?.media_urls?.length; i++) {

                        if (getFileTypeImageVideo(post?.media_urls[i]) === "image" || getFileTypeImageVideo(post?.media_urls[i]) === "video") {
                            const { width, height } = getFileTypeImageVideo(post?.media_urls[i]) === "image"
                                ? await _getImageDimensions(post?.media_urls[i])
                                : await getVideoDimensions(post?.media_urls[i]);

                            const aspectRatio = width / height;
                            const [minRatio, maxRatio] = constants?.media_urls?.allowed_aspect_ratios;
                            const isValidRatio = minRatio <= aspectRatio && aspectRatio <= maxRatio;
                
                            if (!isValidRatio) {
                                errors.push({
                                    network: post?.network,
                                    error: t(`postErrors.invalidAspectRatio`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type),})
                                });
                            }
                        }
                    }
                }

                // if (constants?.media_urls?.required_cover == true) {
                //     for (let i = 0; i < post?.media_urls?.length; i++) {
                //         if (getFileType(post?.media_urls[i]) == "video") {
                //             if (!post?.cover?.url) errors.push({ network: post?.network, error: t(`postErrors.coverRequired`, { network: _getNetworkPostName(post?.network, post?.preferences?.media_product_type)}) });
                //         }
                //     }   
                // }
            }
        }
        
        return errors;
    }

    useEffect(() => {
        setValidationConstants(getPostValidationConstants({ companySocials, twitterLinksAllowed }));
    }, [companySocials, twitterLinksAllowed]);


    return { validate, getErrors, validationConstants, getFirstError, getDescriptionLengthErrors }
}

export default usePostValidator
